import styled from 'styled-components'
import { VideoData } from '../../../interfaces'
import { animated, useSpring } from 'react-spring'
import { useEffect, useState } from 'react'
import { useConnectModal } from '../../../hooks/walletModal'
import { Popover } from '@geist-ui/react'
import { getTimeRemaining } from '../../../utils/time'
import useInterval from '../../../utils/useInterval'
import { useMediaQuery } from '@material-ui/core'
import { Apple } from '@material-ui/icons'
import { Calendar } from '@geist-ui/react-icons'
import { CalendarEvent, buildUrl } from '../../../utils/calendar'
import isMobileCalendar from '../../../utils/isMobile'
import useAuth from '../../../hooks/auth'
import { useSubscription } from '@apollo/client'
import { Query_Root } from '../../../graphql/indexer-graphql-types'
import { BALANCE_SUBSCRIPTION, PRIMARY_SUBSCRIPTION } from '../../../graphql/graphql-queries'
import { ClickAddToCalendarEvent } from '../../../events/social_events'
import log from '../../../events/log'
import { ClickBuyEvent } from '../../../events/revenue_events'
import { db } from '../../../data/FirebaseUtils'
import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'
import { useAtom } from 'jotai'
import { checkoutAtom } from '../../../atoms/Wallet'

type EmptyStateProps = {
  video: VideoData
}

export const EmptyState = ({ video }: EmptyStateProps): JSX.Element => {
  const setVisible = useConnectModal()
  const { profile, isAuthenticated } = useAuth()
  const [buyHover, setBuyHover] = useState(false)
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [hasAllowlist, setHasAllowlist] = useState(false)
  const [allowlistAmount, setAllowlistAmount] = useState(0)
  const [allowlistDiscount, setAllowlistDiscount] = useState<number | undefined>(undefined)
  const isLargeDesktop = useMediaQuery('(min-width: 82em)')
  const [visible, setCalendarVisible] = useState(false)
  const [, checkout] = useAtom(checkoutAtom)
  const primaryData = useSubscription<Query_Root>(PRIMARY_SUBSCRIPTION, {
    variables: {
      mint: video.address,
    },
  }).data

  const primaryMint = primaryData?.mint[0]
  const scheduled_time = primaryMint?.dropTime?.dropTime
    ? new Date(primaryMint?.dropTime?.dropTime)
    : new Date()

  const [live, setIsLive] = useState(scheduled_time <= new Date())

  useInterval(() => {
    const time = getTimeRemaining(scheduled_time)
    if (time.days <= 0 && time.hours <= 0 && time.minutes <= 0 && time.seconds <= 0) {
      setIsLive(true)
    } else {
      setIsLive(false)
    }
  }, 1000)

  const calendarStyle = {
    width: '100%',
    padding: isMobile ? '14px 0' : isLargeDesktop ? '20px 0' : '15px 0',
  }

  const checkAllowlist = async (user: string): Promise<void> => {
    const presaleDoc = await db.presales(video.address).doc(user).get()
    setHasAllowlist(presaleDoc.exists)
    if (presaleDoc.exists) {
      const amount = presaleDoc.data()?.amount ?? 1
      const price = presaleDoc.data()?.price ?? undefined
      setAllowlistAmount(amount)
      setAllowlistDiscount(price)
    } else {
      setAllowlistAmount(0)
      setAllowlistDiscount(undefined)
    }
  }

  useEffect(() => {
    if (isAuthenticated && profile && video.has_presale && !live) {
      checkAllowlist(profile.address)
    } else if (primaryMint?.editionMetadata?.primarySalePrice === 0) {
      setAllowlistAmount(5)
      setAllowlistDiscount(undefined)
    } else {
      setAllowlistAmount(0)
      setAllowlistDiscount(undefined)
    }
  }, [isAuthenticated, video.address])

  const changeVisible = (next: boolean): void => {
    setCalendarVisible(next)
  }

  const openCalendarLink = (event: CalendarEvent, type: string, id: string): void => {
    const url = buildUrl(event, type, id)
    if (!isMobileCalendar() && (url.startsWith('data') || url.startsWith('BEGIN'))) {
      const filename = 'download.ics'
      const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' })

      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      window.open(url, '_blank')
    }
    setCalendarVisible(false)
  }

  const buyHoverState = useSpring({
    boxShadow: buyHover ? '0px 5px 25px rgba(255,255,255,0.2)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: buyHover ? `translate3d(0,-7%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const balanceData = useSubscription<Query_Root>(BALANCE_SUBSCRIPTION, {
    variables: {
      mint: video?.address ?? '',
      user: profile?.address ?? '',
    },
  }).data

  const editionBalance = balanceData?.mint[0]?.tokenAccountsAggregate.aggregate?.sum?.amount

  const openCheckout = async (): Promise<void> => {
    if (!isAuthenticated) {
      setVisible(true)
      return
    }

    const creator = await fbEnrichUsersFromIds([video.created_by])
    checkout({
      data: {
        creator: creator.length > 0 ? creator[0] : undefined,
        video: video,
        primaryInfo: primaryData
          ? {
              sold: primaryMint?.supply || 0,
              price: primaryMint?.editionMetadata?.primarySalePrice || 0,
              supply: primaryMint?.editionMetadata?.maxSupply || 0,
            }
          : undefined,
      },
      isLive: live,
      allowlistAmount,
      allowlistDiscount,
      balance: editionBalance,
    })
  }

  return (
    <OuterDiv>
      <Rank>{`🥇`}</Rank>
      <TextDiv>
        <Title>Be the first collector</Title>
        <Subtitle>Join The Circle for this video.</Subtitle>
      </TextDiv>
      {live ? (
        <BuyButton
          onMouseEnter={() => setBuyHover(true)}
          onMouseLeave={() => setBuyHover(false)}
          style={buyHoverState}
          onClick={() => {
            // It MUST be the primary market, cause once the first edition is
            // sold, this component will be unmounted.
            log(new ClickBuyEvent(video.address, true))

            openCheckout()
          }}
        >
          Buy Edition
        </BuyButton>
      ) : (
        <BuyButton
          onMouseEnter={() => setBuyHover(true)}
          onMouseLeave={() => setBuyHover(false)}
          style={buyHoverState}
          calendar={!video.has_presale || (video.has_presale && isAuthenticated && !hasAllowlist)}
          onClick={() => {
            if (!video.has_presale || (video.has_presale && isAuthenticated && !hasAllowlist)) {
              log(new ClickAddToCalendarEvent())
            } else {
              openCheckout()
            }
          }}
        >
          {!video.has_presale || (video.has_presale && isAuthenticated && !hasAllowlist) ? (
            <Popover
              hideArrow
              trigger={'click'}
              visible={visible}
              placement={'bottomEnd'}
              onVisibleChange={changeVisible}
              style={calendarStyle}
              content={
                <PopoverWrapper>
                  <MenuItem
                    onClick={() =>
                      openCalendarLink(
                        {
                          title: video.title,
                          description: video.description,
                          startTime: scheduled_time,
                        },
                        'apple',
                        video.address
                      )
                    }
                  >
                    <Apple />
                    <MenuItemTitle color="white">Apple Calendar</MenuItemTitle>
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      openCalendarLink(
                        {
                          title: video.title,
                          description: video.description,
                          startTime: scheduled_time,
                        },
                        'google',
                        video.address
                      )
                    }
                  >
                    <Calendar />
                    <MenuItemTitle color="white">Google Calendar</MenuItemTitle>
                  </MenuItem>
                </PopoverWrapper>
              }
            >
              {'Add To Calendar'}
            </Popover>
          ) : isAuthenticated && hasAllowlist ? (
            `Claim ${allowlistAmount} Edition${allowlistAmount === 1 ? '' : 's'}`
          ) : (
            'Check Whitelist Status'
          )}
        </BuyButton>
      )}
    </OuterDiv>
  )
}

export default EmptyState

const OuterDiv = styled.div`
  position: absolute;
  top: calc(20% - 10px);
  left: calc(20% - 10px);
  width: 60%;
  height: 60%;
  background-color: rgba(16, 16, 16, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-radius: 15px;

  @media (min-width: 62em) {
    padding: 20px;
    top: calc(20% - 20px);
    left: calc(20% - 20px);
  }
`

const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Title = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 700;
  text-align: center;

  @media (min-width: 43em) {
    font-size: 24px;
  }

  @media (min-width: 62em) {
    font-size: 20px;
  }

  @media (min-width: 82em) {
    font-size: 24px;
  }
`

const Subtitle = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 500;
  text-align: center;

  @media (min-width: 43em) {
    font-size: 16px;
  }

  @media (min-width: 62em) {
    font-size: 16px;
  }

  @media (min-width: 82em) {
    font-size: 18px;
  }
`

const Rank = styled.div`
  font-size: 85px;
  text-align: center;
  margin-bottom: -25px;

  @media (min-width: 43em) {
    font-size: 115px;
    margin-bottom: -50px;
  }

  @media (min-width: 62em) {
    font-size: 85px;
    margin-bottom: -25px;
  }

  @media (min-width: 82em) {
    font-size: 115px;
    margin-bottom: -50px;
  }
`

const BuyButton = styled(animated.div)<{ calendar?: boolean }>`
  background: white;
  width: 100%;
  border-radius: 15px;
  color: black;
  text-align: center;
  align-items: center;
  font-weight: 700;
  font-size: 16px;
  padding: ${(props) => (props.calendar ? '' : '14px 0')};
  cursor: pointer;

  @media (min-width: 43em) {
    padding: ${(props) => (props.calendar ? '' : '15px 0')};
  }

  @media (min-width: 62em) {
    font-size: 18px;
  }

  @media (min-width: 82em) {
    padding: ${(props) => (props.calendar ? '' : '20px 0')};
    font-size: 18px;
  }
`

const PopoverWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 225px;
`

const MenuItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: 12px 20px;
  align-items: center;
  cursor: pointer;

  :hover {
    background: #232323;
  }
`

const MenuItemTitle = styled.div<{ color: string }>`
  font-weight: 400;
  color: ${(props) => props.color};
  font-size: 16px;
`
